// ============================================
// RAG Pipeline Playground – domain logic (no DOM)
// ============================================

export type ChunkConfig = {
  size: number;
  overlap: number;
};

export type Chunk = {
  text: string;
  start: number;
  end: number;
  index: number;
};

export type SimilarityResult = {
  chunk: Chunk;
  score: number;
};

// ============================================
// Chunking
// ============================================

export function chunkByCharacter(text: string, config: ChunkConfig): Chunk[] {
  if (!text || config.size <= 0) return [];
  if (config.overlap >= config.size) {
    config = { ...config, overlap: 0 };
  }

  const chunks: Chunk[] = [];
  let start = 0;
  let index = 0;

  while (start < text.length) {
    const end = Math.min(start + config.size, text.length);
    chunks.push({
      text: text.slice(start, end),
      start,
      end,
      index,
    });
    const step = config.size - config.overlap;
    start += step > 0 ? step : config.size;
    index++;
  }

  return chunks;
}

export function chunkByParagraph(text: string, maxSize: number = 500): Chunk[] {
  if (!text) return [];

  const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());
  const chunks: Chunk[] = [];
  let currentParts: string[] = [];
  let currentLength = 0;
  let currentStart = 0;
  let index = 0;

  // Track position in original text
  let pos = 0;

  for (const para of paragraphs) {
    // Find where this paragraph starts in the original text
    const paraStart = text.indexOf(para, pos);
    pos = paraStart + para.length;

    if (currentLength + para.length > maxSize && currentParts.length > 0) {
      const chunkText = currentParts.join('\n\n');
      chunks.push({
        text: chunkText,
        start: currentStart,
        end: currentStart + chunkText.length,
        index,
      });
      index++;
      currentParts = [];
      currentLength = 0;
      currentStart = paraStart;
    }

    if (currentParts.length === 0) {
      currentStart = paraStart;
    }
    currentParts.push(para);
    currentLength += para.length;
  }

  if (currentParts.length > 0) {
    const chunkText = currentParts.join('\n\n');
    chunks.push({
      text: chunkText,
      start: currentStart,
      end: currentStart + chunkText.length,
      index,
    });
  }

  return chunks;
}

// ============================================
// Similarity (keyword-based, no real embeddings)
// ============================================

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 2);
}

function termFrequency(tokens: string[]): Map<string, number> {
  const tf = new Map<string, number>();
  for (const token of tokens) {
    tf.set(token, (tf.get(token) ?? 0) + 1);
  }
  return tf;
}

export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;
  let dot = 0;
  let magA = 0;
  let magB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    magA += a[i] * a[i];
    magB += b[i] * b[i];
  }
  const denom = Math.sqrt(magA) * Math.sqrt(magB);
  return denom === 0 ? 0 : dot / denom;
}

export function findSimilar(
  query: string,
  chunks: Chunk[],
  topK: number = 3
): SimilarityResult[] {
  if (!query.trim() || chunks.length === 0) return [];

  // Build vocabulary from all chunks + query
  const allTokenSets = chunks.map((c) => tokenize(c.text));
  const queryTokens = tokenize(query);
  const vocabSet = new Set<string>();
  for (const tokens of allTokenSets) {
    for (const t of tokens) vocabSet.add(t);
  }
  for (const t of queryTokens) vocabSet.add(t);
  const vocab = [...vocabSet];

  // Convert to TF vectors
  function toVector(tokens: string[]): number[] {
    const tf = termFrequency(tokens);
    return vocab.map((term) => tf.get(term) ?? 0);
  }

  const queryVec = toVector(queryTokens);

  const scored: SimilarityResult[] = chunks.map((chunk) => {
    const chunkVec = toVector(tokenize(chunk.text));
    return { chunk, score: cosineSimilarity(queryVec, chunkVec) };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, topK);
}

// ============================================
// Statistics
// ============================================

export type ChunkStats = {
  count: number;
  avgLength: number;
  minLength: number;
  maxLength: number;
  overlapPercent: number;
  totalCoverage: number;
};

export function computeStats(chunks: Chunk[], config: ChunkConfig): ChunkStats {
  if (chunks.length === 0) {
    return {
      count: 0,
      avgLength: 0,
      minLength: 0,
      maxLength: 0,
      overlapPercent: 0,
      totalCoverage: 0,
    };
  }

  const lengths = chunks.map((c) => c.text.length);
  const total = lengths.reduce((a, b) => a + b, 0);

  return {
    count: chunks.length,
    avgLength: Math.round(total / chunks.length),
    minLength: Math.min(...lengths),
    maxLength: Math.max(...lengths),
    overlapPercent:
      config.size > 0 ? Math.round((config.overlap / config.size) * 100) : 0,
    totalCoverage:
      chunks.length > 0 ? chunks[chunks.length - 1].end - chunks[0].start : 0,
  };
}

// ============================================
// Sample texts
// ============================================

export const SAMPLE_TEXTS = {
  'security-advisory': `CVE-2024-3094: XZ Utils Backdoor

Severity: Critical (CVSS 10.0)
Affected versions: xz 5.6.0, 5.6.1
Fixed in: xz 5.6.2

A supply chain compromise was discovered in XZ Utils, a widely-used compression library. Malicious code was introduced through a series of obfuscated commits to the upstream repository by a trusted maintainer account. The backdoor targeted the build process of liblzma, injecting code that modified the behavior of OpenSSH's sshd when linked against the compromised library.

The backdoor allowed unauthorized remote access by intercepting SSH authentication. It specifically targeted x86-64 Linux systems using glibc and systemd. The malicious payload was hidden in test fixture files and activated through a multi-stage build script modification.

Mitigation: Downgrade to xz 5.4.x or upgrade to 5.6.2. Audit systems running affected versions for signs of unauthorized SSH access. Check if sshd links against liblzma with: ldd $(which sshd) | grep liblzma.

CVE-2024-6387: regreSSHion — OpenSSH Race Condition

Severity: High (CVSS 8.1)
Affected versions: OpenSSH 8.5p1 through 9.7p1 (glibc-based)
Fixed in: OpenSSH 9.8p1

A signal handler race condition in OpenSSH's sshd allows unauthenticated remote code execution as root on glibc-based Linux systems. The vulnerability exists in the SIGALRM handler called during the LoginGraceTime period. An attacker can trigger the race condition by failing to authenticate within the grace period.

Exploitation requires approximately 10,000 connection attempts, making it detectable through monitoring. The attack takes several hours against a 32-bit target and has not been demonstrated against 64-bit systems with ASLR, though this does not rule it out.

Mitigation: Upgrade to OpenSSH 9.8p1. As a temporary workaround, set LoginGraceTime to 0 in sshd_config. Monitor for unusual volumes of failed SSH connections.`,

  'incident-report': `Incident Report: Unauthorized Access Attempt

Date: 2026-03-15
Severity: High
Status: Contained

Timeline of Events:

08:15 UTC - Monitoring detected multiple failed SSH login attempts from IP 203.0.113.44 against web-server-01 (10.0.1.15). Source IP is not in any known threat intelligence feed.

08:22 UTC - Brute force threshold exceeded. Wazuh alert rule 5763 triggered at level 12. Automated firewall block applied to source IP.

08:45 UTC - Integrity monitoring detected modification to /etc/passwd on web-server-01. Wazuh alert rule 550 triggered at level 7. New user "svc_backup" added with UID 1001.

09:01 UTC - SOC analyst investigated and confirmed the new user was created by an authorized administrator as part of a planned backup service deployment. Change ticket CT-4521 verified.

09:15 UTC - False positive confirmed. Firewall block on 203.0.113.44 maintained as a precaution pending further analysis of the brute force attempt. No evidence of successful compromise.

Root Cause Analysis:

The brute force attempt and the /etc/passwd modification were unrelated events that occurred in close temporal proximity. The SSH brute force was a routine internet scan. The file modification was a legitimate administrative action.

Lessons Learned:

Correlation of temporally adjacent alerts requires verification before escalation. Automated blocking successfully mitigated the brute force attempt. The integrity monitoring alert functioned as designed and was resolved through change ticket verification.`,

  custom: '',
};
